import React, { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { useReferProgram } from "@/hooks/followup/useReferProgram";
import { useDebounce } from "@/hooks/use-debounce";
import {
  Widget,
  WidgetHeader,
  WidgetContent,
} from "@/components/consultation/shared/Widget";
import { Textarea } from "@/components/ui/textarea";
import { useAutoSaveInit } from "@/hooks/useAutoSaveInit";

interface InternalNotesCardProps {
  appointmentId: string;
  isCompleted?: boolean;
  parentType?: "prescription" | "meal_plan";
}

export const InternalNotesCard = ({
  appointmentId,
  isCompleted = false,
  parentType = "prescription",
}: InternalNotesCardProps) => {
  const {
    data: programData,
    update,
    isUpdating,
  } = useReferProgram(appointmentId, parentType);
  const { isInitialized, markInitialized } = useAutoSaveInit([
    appointmentId,
    parentType,
  ]);

  const [nutritionistNotes, setNutritionistNotes] = useState("");
  const [physioNotes, setPhysioNotes] = useState("");
  const [careManagerNotes, setCareManagerNotes] = useState("");

  useEffect(() => {
    if (programData && !isInitialized.current) {
      const firstFieldValue =
        parentType === "meal_plan"
          ? programData.doctor_notes
          : programData.nutritionist_notes;
      setNutritionistNotes(firstFieldValue || "");
      setPhysioNotes(programData.physiotherapist_notes || "");
      setCareManagerNotes(programData.care_manager_notes || "");
      markInitialized();
    }
  }, [programData, isInitialized, markInitialized, parentType]);

  const debouncedNutri = useDebounce(nutritionistNotes, 1000);
  const debouncedPhysio = useDebounce(physioNotes, 1000);
  const debouncedCare = useDebounce(careManagerNotes, 1000);

  useEffect(() => {
    if (!programData) return;
    if (!isInitialized.current) return;

    const currentFirstFieldValue =
      parentType === "meal_plan"
        ? programData.doctor_notes
        : programData.nutritionist_notes;

    const hasChanges =
      currentFirstFieldValue !== debouncedNutri ||
      programData.physiotherapist_notes !== debouncedPhysio ||
      programData.care_manager_notes !== debouncedCare;

    if (!hasChanges) return;

    const updatePayload =
      parentType === "meal_plan"
        ? {
            doctor_notes: debouncedNutri,
            physiotherapist_notes: debouncedPhysio,
            care_manager_notes: debouncedCare,
          }
        : {
            nutritionist_notes: debouncedNutri,
            physiotherapist_notes: debouncedPhysio,
            care_manager_notes: debouncedCare,
          };

    update({ id: programData.id, data: updatePayload });
  }, [
    debouncedNutri,
    debouncedPhysio,
    debouncedCare,
    programData,
    update,
    parentType,
  ]);

  if (!programData) {
    return (
      <Widget className="opacity-60">
        <WidgetHeader title="Internal Notes" />
        <WidgetContent className="pt-3">
          <p className="z-secondary text-muted-foreground text-center py-6">
            Please select a Program above to enable internal notes.
          </p>
        </WidgetContent>
      </Widget>
    );
  }

  const primaryDoctorLabel =
    parentType === "meal_plan" ? "For Primary Doctor" : "For Nutritionist";

  return (
    <Widget>
      <WidgetHeader
        title="Internal Notes"
        hasInfo
        extras={
          isUpdating ? (
            <Loader2 className="size-3 animate-spin text-muted-foreground" />
          ) : undefined
        }
      />
      <WidgetContent className="pt-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <label className="z-label text-muted-foreground">
              {primaryDoctorLabel}
            </label>
            <Textarea
              value={nutritionistNotes}
              onChange={(e) => setNutritionistNotes(e.target.value)}
              className="min-h-[120px] resize-none"
              placeholder="Type note here..."
              disabled={isCompleted}
            />
          </div>
          <div className="space-y-1.5">
            <label className="z-label text-muted-foreground">
              For Physiotherapist
            </label>
            <Textarea
              value={physioNotes}
              onChange={(e) => setPhysioNotes(e.target.value)}
              className="min-h-[120px] resize-none"
              placeholder="Type note here..."
              disabled={isCompleted}
            />
          </div>
          <div className="space-y-1.5">
            <label className="z-label text-muted-foreground">
              For Care Manager
            </label>
            <Textarea
              value={careManagerNotes}
              onChange={(e) => setCareManagerNotes(e.target.value)}
              className="min-h-[120px] resize-none"
              placeholder="Type note here..."
              disabled={isCompleted}
            />
          </div>
        </div>
      </WidgetContent>
    </Widget>
  );
};
